/**
 * ZON-275 — a school's panel opened from the Notifications page, so a family
 * can read about a newly matched program before deciding to add it. Built
 * from the same pieces as the School Search and My Schools panels: the
 * branded header and Program details. A side sheet on desktop, a bottom
 * drawer on phones (same pattern as SchoolPortfolioDetail).
 *
 * Opens for any school in the list. A new fit is not on My Schools yet: the
 * panel offers Add and shows the recommendation's tier, score and reason. A
 * school in the news is already saved: the panel links to My Schools and
 * shows its recent news. The header shows at once; program details load
 * behind it. No notes: My Schools is the place for those.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PreviewSchool } from "@/components/news/newsFeed";
import {
  fetchCollege,
  fetchSchoolById,
  type CollegeDetail,
  type SchoolResult,
} from "@/services/schools";
import { SchoolBrandHeader } from "./SchoolBrandHeader";
import { SchoolNewsCard } from "./SchoolNewsCard";
import { SchoolProgramDetailsPanel } from "./SchoolProgramDetailsPanel";

interface SchoolPreviewPanelProps {
  school: PreviewSchool | null;
  onClose: () => void;
  onAdd: (school: PreviewSchool) => void;
  adding: boolean;
}

function PreviewBody({
  school,
  onAdd,
  adding,
}: {
  school: PreviewSchool;
  onAdd: (school: PreviewSchool) => void;
  adding: boolean;
}) {
  const [detail, setDetail] = useState<SchoolResult | null>(null);
  const [college, setCollege] = useState<CollegeDetail | null>(null);
  const [collegeLoading, setCollegeLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setCollege(null);
    setFailed(false);
    setCollegeLoading(false);

    async function load() {
      try {
        const loaded = await fetchSchoolById(school.teamId);
        if (cancelled) return;
        setDetail(loaded);
        if (!loaded.college_id) return;
        setCollegeLoading(true);
        try {
          const loadedCollege = await fetchCollege(loaded.college_id);
          if (!cancelled) setCollege(loadedCollege);
        } catch (error) {
          console.error("Failed to load college details:", error);
        } finally {
          if (!cancelled) setCollegeLoading(false);
        }
      } catch (error) {
        console.error("Failed to load school details:", error);
        if (!cancelled) setFailed(true);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [school.teamId]);

  const name = school.schoolName || detail?.schoolName || "School";
  const branded = {
    schoolName: name,
    division: school.division ?? detail?.division,
    city: college?.city || school.city,
    state: school.state,
    logoUrl: college?.logo_url || school.logoUrl,
    brandColorPrimary: college?.brand_color_primary,
    brandColorSecondary: college?.brand_color_secondary,
  };

  return (
    <div className="overflow-hidden">
      <SchoolBrandHeader school={branded} fit={school.fit ?? null} variant="detail" />
      <div className="border-b border-border bg-white p-4 sm:px-6">
        {school.saved ? (
          <Button asChild variant="outline" className="h-11 w-full gap-2 text-xs">
            <Link to={`/pipeline?school=${encodeURIComponent(school.teamId)}`}>
              Open in My Schools <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => onAdd(school)}
            disabled={adding}
            className="h-11 w-full gap-2 text-xs"
          >
            <Star className="h-4 w-4" />
            {adding ? "Adding…" : "Add to My Schools"}
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3 p-4 sm:px-6">
        {school.reason && (
          <section className="rounded-2xl border border-lime-border bg-lime-soft p-4 text-forest-soft">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-muted-foreground">
              Why it may fit
            </p>
            <p className="mt-1.5 text-[11px] leading-relaxed">{school.reason}</p>
          </section>
        )}
        {school.saved && <SchoolNewsCard teamId={school.teamId} />}
        {detail && (
          <SchoolProgramDetailsPanel
            school={detail}
            userGenderLabel={school.gender || "All"}
            collegeData={college}
            collegeLoading={collegeLoading}
          />
        )}
        {!detail && !failed && <p className="text-[11px] text-subtle">Loading program details…</p>}
        {failed && (
          <p className="rounded-2xl border border-border bg-white p-4 text-[11px] text-forest">
            Program details could not be loaded.
          </p>
        )}
      </div>
    </div>
  );
}

export function SchoolPreviewPanel({ school, onClose, onAdd, adding }: SchoolPreviewPanelProps) {
  const isMobile = useIsMobile();
  // Screen-reader title for the dialog; the visible name is the header's heading.
  const title = school?.schoolName ? `${school.schoolName} details` : "School details";

  if (isMobile) {
    return (
      <Drawer
        open={!!school}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DrawerContent className="max-h-[92vh] overflow-hidden rounded-t-[24px] border-border bg-surface">
          <DrawerHeader className="sr-only">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>
              Program details for a school that fits your profile
            </DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 overflow-y-auto">
            {school && <PreviewBody school={school} onAdd={onAdd} adding={adding} />}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet
      open={!!school}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full max-w-[500px] flex-col overflow-hidden border-l-0 bg-surface p-0 sm:max-w-[500px]"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>Program details for a school that fits your profile</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {school && <PreviewBody school={school} onAdd={onAdd} adding={adding} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
